import React, { useEffect, useState, useMemo } from "react";
import { customerApi, setCustomerToken } from "@/lib/customerApi";
import { toast } from "sonner";
import Brand from "@/components/Brand";
import {
    UtensilsCrossed, Bell, Clock, ChefHat, Plus, Minus, ShoppingCart,
    LogOut, AlertTriangle, Loader2, CheckCircle2, Send, X, BellRing,
} from "lucide-react";

const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.74 3.28-8.1Z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.77c-.98.66-2.24 1.05-3.72 1.05-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.73.13-1.43.35-2.09V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
    </svg>
);

// Estrae il codice tavolo dal querystring (?t=TAV-12-AB3F) o da localStorage
const readTableCodeFromUrl = () => {
    try {
        const u = new URL(window.location.href);
        const c = (u.searchParams.get("t") || "").trim().toUpperCase();
        if (c) {
            localStorage.setItem("st_table_code", c);
            return c;
        }
    } catch (_e) { /* */ }
    return (localStorage.getItem("st_table_code") || "").trim().toUpperCase();
};

const CustomerEntry = () => {
    const [step, setStep] = useState("loading"); // loading, login, checkin, hub
    const [me, setMe] = useState(null);
    const [tableNumber, setTableNumber] = useState("");
    const [tableCode, setTableCode] = useState("");
    const [tablePreview, setTablePreview] = useState(null); // info tavolo precaricata da QR
    const [useCodeMode, setUseCodeMode] = useState(false);
    const [tab, setTab] = useState("menu"); // menu, order, call
    const [menu, setMenu] = useState({ categories: [], items: [], allergens: [] });
    const [activeCat, setActiveCat] = useState(null);
    const [order, setOrder] = useState(null);
    const [calling, setCalling] = useState(false);
    const [callNote, setCallNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Carica eventuale codice tavolo dal QR all'avvio
    useEffect(() => {
        const code = readTableCodeFromUrl();
        if (code) {
            setTableCode(code);
            setUseCodeMode(true);
            customerApi.get(`/customer/table-info/${encodeURIComponent(code)}`)
                .then(({ data }) => setTablePreview(data))
                .catch(() => {
                    setTablePreview({ invalid: true });
                    localStorage.removeItem("st_table_code");
                });
        }
    }, []);

    const loadMe = async () => {
        try {
            const { data } = await customerApi.get("/customer/me");
            setMe(data);
            if (!data.table_id) {
                // Se ho un codice tavolo dal QR e l'utente è loggato, faccio check-in automatico
                const code = readTableCodeFromUrl();
                if (code) {
                    try {
                        await customerApi.post("/customer/checkin", { table_code: code });
                        toast.success("Check-in automatico riuscito");
                        localStorage.removeItem("st_table_code");
                        const r = await customerApi.get("/customer/me");
                        setMe(r.data);
                        setStep("hub");
                        return;
                    } catch (e) {
                        toast.error(e?.response?.data?.detail || "Codice tavolo non valido");
                    }
                }
                setStep("checkin");
            } else {
                setStep("hub");
            }
        } catch (_e) { setStep("login"); }
    };

    useEffect(() => { loadMe(); }, []);

    useEffect(() => {
        if (step !== "hub") return;
        loadMenu();
        loadOrder();
        const id = setInterval(loadOrder, 6000);
        const menuId = setInterval(loadMenu, 30000); // refresh per cogliere TERMINATO live + nuovi piatti
        // Quando l'utente torna sulla scheda del browser, forza un refresh menu+ordine
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                loadMenu();
                loadOrder();
            }
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            clearInterval(id);
            clearInterval(menuId);
            document.removeEventListener("visibilitychange", onVisible);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const loadMenu = async () => {
        try {
            const { data } = await customerApi.get("/customer/menu");
            setMenu(data);
            if (data.categories.length && !activeCat) setActiveCat(data.categories[0].id);
        } catch (_e) { /* */ }
    };

    const loadOrder = async () => {
        try {
            const { data } = await customerApi.get("/customer/order");
            setOrder(data.order);
        } catch (_e) { /* */ }
    };

    const loginWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        // Preserva eventuale codice tavolo letto dal QR per fare check-in automatico al ritorno
        const code = readTableCodeFromUrl();
        const callback = code
            ? `${window.location.origin}/c/auth/callback?t=${encodeURIComponent(code)}`
            : `${window.location.origin}/c/auth/callback`;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(callback)}`;
    };

    const checkin = async (e) => {
        e?.preventDefault();
        const useCode = useCodeMode && tableCode.trim();
        if (!useCode && !tableNumber.trim()) return;
        setSubmitting(true);
        try {
            if (useCode) {
                await customerApi.post("/customer/checkin", { table_code: tableCode.trim().toUpperCase() });
                toast.success("Check-in tavolo riuscito");
                localStorage.removeItem("st_table_code");
            } else {
                await customerApi.post("/customer/checkin", { table_number: tableNumber.trim() });
                toast.success(`Benvenuto al tavolo ${tableNumber}`);
            }
            await loadMe();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        setSubmitting(false);
    };

    const callWaiter = async () => {
        setCalling(true);
        try {
            await customerApi.post("/customer/call-waiter", { note: callNote });
            toast.success("Cameriere chiamato! Arriverà a breve.");
            setCallNote("");
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        setCalling(false);
    };

    const addItem = async (item) => {
        try {
            await customerApi.post("/customer/order/items", { menu_item_id: item.id, quantity: 1 });
            toast.success(`${item.name} aggiunto`);
            loadOrder();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };

    const sendOrder = async () => {
        if (!window.confirm("Inviare l'ordine in cucina?")) return;
        try {
            await customerApi.post("/customer/order/send");
            toast.success("Ordine inviato in cucina!");
            loadOrder();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };

    const logout = () => {
        setCustomerToken(null);
        document.cookie = "customer_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        window.location.href = "/c";
    };

    const filteredItems = menu.items.filter((i) => i.category_id === activeCat);
    const orderItems = order?.items || [];
    const pendingItems = orderItems.filter((it) => it.status === "in_attesa");
    // "Ricevuto in cucina, in attesa di accettazione" — primo step per il cliente
    const inKitchenWaiting = orderItems.filter((it) => it.status === "in_cucina");
    const inKitchen = orderItems.filter((it) => it.status === "in_preparazione");
    const ready = orderItems.filter((it) => it.status === "pronto");
    const delivered = orderItems.filter((it) => it.status === "consegnato");

    // Calcolo totale conto live (somma item × quantità, esclude solo eventuali item futuri non in menu)
    const liveTotal = orderItems.reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0);
    const sentItems = orderItems.filter((it) => it.status !== "in_attesa");

    if (step === "loading") {
        return (
            <div className="min-h-screen bg-deep flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-brand animate-spin" />
            </div>
        );
    }

    if (step === "login") {
        return (
            <div className="min-h-screen bg-deep flex flex-col items-center justify-center p-6">
                <div className="max-w-sm w-full bg-surface border border-[#1f2c44] rounded-2xl p-8 text-center">
                    <Brand size="lg" />
                    <div className="mt-8 mb-2 text-xs uppercase tracking-[0.3em] text-brand">Area Clienti</div>
                    <h1 className="font-display text-3xl font-bold text-warm mb-2">Benvenuto</h1>
                    <p className="text-elegant text-sm mb-8">
                        Accedi con il tuo account Google per consultare il menu, vedere l'ordine
                        in tempo reale e chiamare il cameriere.
                    </p>
                    <button
                        onClick={loginWithGoogle}
                        data-testid="customer-google-login"
                        className="w-full h-14 bg-white text-deep font-display font-semibold rounded-md flex items-center justify-center gap-3 hover:bg-gray-100 transition-colors"
                    >
                        <GoogleIcon />
                        <span>Accedi con Google</span>
                    </button>
                </div>
            </div>
        );
    }

    if (step === "checkin") {
        const previewOk = tablePreview && !tablePreview.invalid;
        return (
            <div className="min-h-screen bg-deep flex flex-col items-center justify-center p-6">
                <div className="max-w-sm w-full bg-surface border border-[#1f2c44] rounded-2xl p-8 text-center">
                    <Brand size="lg" />
                    <div className="mt-8 mb-2 text-xs uppercase tracking-[0.3em] text-brand">Check-in tavolo</div>
                    <h1 className="font-display text-2xl font-bold text-warm mb-2">Ciao {me?.name?.split(" ")[0]}!</h1>

                    {previewOk && (
                        <div className="mb-4 rounded-md border border-brand bg-brand-soft p-3">
                            <div className="text-[10px] uppercase tracking-wider text-elegant">Tavolo dal QR</div>
                            <div className="font-display font-bold text-2xl text-brand">N° {tablePreview.number}</div>
                            <div className="text-xs text-elegant">{tablePreview.short_code} · {tablePreview.seats} posti</div>
                        </div>
                    )}
                    {tablePreview?.invalid && (
                        <div className="mb-4 rounded-md border border-red-700/50 bg-red-900/20 p-3 text-xs text-red-300">
                            Codice tavolo non valido. Inserisci numero o codice manualmente.
                        </div>
                    )}

                    <p className="text-elegant text-sm mb-4">
                        {previewOk
                            ? "Conferma per accedere al menu, all'ordine e chiamare il cameriere."
                            : "Inserisci il numero del tuo tavolo o il codice stampato sul QR."}
                    </p>

                    <div className="flex gap-2 mb-4">
                        <button onClick={() => setUseCodeMode(false)} className={`flex-1 h-10 rounded-md text-xs uppercase tracking-wider border ${!useCodeMode ? "border-brand bg-brand-soft text-brand" : "border-[#1f2c44] text-elegant"}`}>Numero</button>
                        <button onClick={() => setUseCodeMode(true)} className={`flex-1 h-10 rounded-md text-xs uppercase tracking-wider border ${useCodeMode ? "border-brand bg-brand-soft text-brand" : "border-[#1f2c44] text-elegant"}`}>Codice</button>
                    </div>

                    <form onSubmit={checkin} className="space-y-4">
                        {useCodeMode ? (
                            <input
                                type="text"
                                value={tableCode}
                                onChange={(e) => setTableCode(e.target.value.toUpperCase())}
                                placeholder="TAV-12-AB3F"
                                required
                                data-testid="customer-table-code-input"
                                className="w-full h-16 px-4 text-center text-xl font-mono font-bold tracking-wider bg-deep border-2 border-[#1f2c44] rounded-md text-warm focus:border-brand focus:outline-none"
                            />
                        ) : (
                            <input
                                type="text"
                                inputMode="numeric"
                                value={tableNumber}
                                onChange={(e) => setTableNumber(e.target.value)}
                                placeholder="N° tavolo"
                                required
                                data-testid="customer-table-input"
                                className="w-full h-16 px-4 text-center text-2xl font-display font-bold bg-deep border-2 border-[#1f2c44] rounded-md text-warm focus:border-brand focus:outline-none"
                            />
                        )}
                        <button type="submit" disabled={submitting} data-testid="customer-checkin-button"
                            className="w-full h-14 bg-brand hover:bg-brand-light text-deep font-display font-bold uppercase tracking-wider rounded-md disabled:opacity-50">
                            {submitting ? "Verifico..." : (previewOk ? `Conferma tavolo ${tablePreview.number}` : "Entra")}
                        </button>
                    </form>
                    <button onClick={logout} className="mt-6 text-xs text-elegant hover:text-warm flex items-center gap-1 mx-auto">
                        <LogOut className="w-3 h-3" /> Esci
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-deep text-warm pb-24" data-testid="customer-hub">
            {/* Header */}
            <header className="bg-surface border-b border-[#1f2c44] px-4 py-3 sticky top-0 z-30">
                <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <Brand size="sm" showSubtitle={false} />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-elegant">Tavolo</div>
                            <div className="font-display text-2xl font-bold text-brand leading-none" data-testid="customer-table-number">{me?.table_number}</div>
                        </div>
                        {me?.picture ? (
                            <img src={me.picture} alt={me.name} className="w-10 h-10 rounded-full border border-brand" />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-brand-soft border border-brand text-brand font-bold flex items-center justify-center">{me?.name?.[0]?.toUpperCase()}</div>
                        )}
                    </div>
                </div>
            </header>

            {/* Notify ready items */}
            {ready.length > 0 && (
                <div className="max-w-3xl mx-auto mt-3 px-4">
                    <div className="bg-brand-soft border border-brand rounded-md p-3 flex items-center gap-2 animate-pulse">
                        <CheckCircle2 className="w-5 h-5 text-brand" />
                        <div className="text-sm">
                            <strong className="text-brand">{ready.length} piatti pronti</strong> — il cameriere sta arrivando.
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-3xl mx-auto px-4 mt-4">
                {/* Tabs */}
                <div className="flex bg-surface border border-[#1f2c44] rounded-full overflow-hidden mb-4 p-1">
                    {[
                        { id: "menu", label: "Menu", icon: UtensilsCrossed },
                        { id: "order", label: "Ordine", icon: ShoppingCart, badge: orderItems.length },
                        { id: "call", label: "Cameriere", icon: Bell },
                    ].map((t) => {
                        const Icon = t.icon;
                        return (
                            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                                className={`flex-1 h-12 rounded-full text-sm flex items-center justify-center gap-2 font-medium transition-colors relative ${
                                    tab === t.id ? "bg-brand text-deep" : "text-elegant"
                                }`}>
                                <Icon className="w-4 h-4" /> {t.label}
                                {t.badge > 0 && (
                                    <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${tab === t.id ? "bg-deep text-brand" : "bg-brand text-deep"}`}>{t.badge}</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {tab === "menu" && (
                    <div data-testid="customer-menu">
                        <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-4 px-4">
                            {menu.categories.map((c) => (
                                <button key={c.id} onClick={() => setActiveCat(c.id)} data-testid={`cust-cat-${c.id}`}
                                    className={`h-10 px-4 rounded-full whitespace-nowrap text-xs uppercase tracking-wider border ${
                                        activeCat === c.id ? "bg-brand text-deep border-brand" : "border-[#1f2c44] text-elegant"
                                    }`}>{c.name}</button>
                            ))}
                        </div>
                        <div className="space-y-2">
                            {filteredItems.map((it) => {
                                const terminato = it.available === false;
                                return (
                                <div key={it.id} className={`bg-surface border rounded-lg p-4 flex justify-between gap-3 relative ${terminato ? "border-red-600/40 opacity-70" : "border-[#1f2c44]"}`} data-testid={`cust-item-${it.id}`}>
                                    {terminato && (
                                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold bg-red-600 text-white animate-pulse">
                                            TERMINATO
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className={`font-display font-bold ${terminato ? "text-elegant line-through" : ""}`}>{it.name}</div>
                                        {it.description && <div className="text-xs text-elegant mt-1">{it.description}</div>}
                                        {it.allergens?.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {it.allergens.map((a) => (
                                                    <span key={a} className="text-[9px] uppercase tracking-wider bg-amber-900/30 text-amber-400 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                        <AlertTriangle className="w-2.5 h-2.5" />{a}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <div className={`font-display font-bold whitespace-nowrap ${terminato ? "text-elegant line-through" : "text-brand"}`}>€ {it.price.toFixed(2)}</div>
                                        {me?.settings?.ordering_enabled && !terminato && (
                                            <button onClick={() => addItem(it)} data-testid={`add-${it.id}`}
                                                className="w-9 h-9 rounded-full bg-brand text-deep flex items-center justify-center hover:bg-brand-light">
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {tab === "order" && (
                    <div data-testid="customer-order-tab">
                        {!order || orderItems.length === 0 ? (
                            <div className="bg-surface border border-[#1f2c44] rounded-lg p-10 text-center">
                                <ShoppingCart className="w-10 h-10 text-brand mx-auto mb-3 opacity-60" />
                                <div className="font-display text-lg font-bold text-warm mb-2">Nessun ordine</div>
                                <div className="text-elegant text-sm">
                                    {me?.settings?.ordering_enabled
                                        ? "Aggiungi piatti dal menu o chiama il cameriere."
                                        : "Chiama il cameriere per ordinare."}
                                </div>
                            </div>
                        ) : (
                            <>
                                {pendingItems.length > 0 && (
                                    <div className="bg-surface border border-[#1f2c44] rounded-lg overflow-hidden mb-3">
                                        <div className="px-4 py-2 bg-deep border-b border-[#1f2c44] text-[10px] uppercase tracking-wider text-elegant">In attesa di invio</div>
                                        <div className="divide-y divide-[#1f2c44]">
                                            {pendingItems.map((it) => (
                                                <div key={it.id} className="p-3 flex justify-between text-sm">
                                                    <div>{it.name} <span className="text-elegant">×{it.quantity}</span></div>
                                                </div>
                                            ))}
                                        </div>
                                        {me?.settings?.ordering_enabled && (
                                            <button onClick={sendOrder} data-testid="customer-send-order"
                                                className="w-full h-12 bg-brand text-deep font-bold uppercase tracking-wider flex items-center justify-center gap-2">
                                                <Send className="w-4 h-4" /> Invia in cucina
                                            </button>
                                        )}
                                    </div>
                                )}
                                {inKitchenWaiting.length > 0 && (
                                    <div className="bg-blue-900/15 border border-blue-700/30 rounded-lg overflow-hidden mb-3" data-testid="customer-in-kitchen-waiting">
                                        <div className="px-4 py-2 border-b border-blue-700/30 text-[10px] uppercase tracking-wider text-blue-300 flex items-center gap-1">
                                            <ChefHat className="w-3 h-3" /> Ricevuti in cucina · in attesa di essere accettati
                                        </div>
                                        <div className="divide-y divide-blue-700/20">
                                            {inKitchenWaiting.map((it) => (
                                                <div key={it.id} className="p-3 flex justify-between text-sm items-center">
                                                    <div>{it.name} <span className="text-elegant">×{it.quantity}</span></div>
                                                    <Loader2 className="w-3 h-3 text-blue-400 animate-spin" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {inKitchen.length > 0 && (
                                    <div className="bg-amber-900/15 border border-amber-700/30 rounded-lg overflow-hidden mb-3">
                                        <div className="px-4 py-2 border-b border-amber-700/30 text-[10px] uppercase tracking-wider text-amber-300 flex items-center gap-1">
                                            <ChefHat className="w-3 h-3" /> Accettati e in preparazione
                                        </div>
                                        <div className="divide-y divide-amber-700/20">
                                            {inKitchen.map((it) => (
                                                <div key={it.id} className="p-3 flex justify-between text-sm items-center">
                                                    <div>{it.name} <span className="text-elegant">×{it.quantity}</span></div>
                                                    <Loader2 className="w-3 h-3 text-amber-400 animate-spin" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {ready.length > 0 && (
                                    <div className="bg-brand-soft border border-brand rounded-lg overflow-hidden mb-3">
                                        <div className="px-4 py-2 border-b border-brand/30 text-[10px] uppercase tracking-wider text-brand flex items-center gap-1">
                                            <CheckCircle2 className="w-3 h-3" /> Pronti — il cameriere sta per portarli
                                        </div>
                                        <div className="divide-y divide-brand/30">
                                            {ready.map((it) => (
                                                <div key={it.id} className="p-3 flex justify-between text-sm">
                                                    <div className="text-warm">{it.name} <span className="text-elegant">×{it.quantity}</span></div>
                                                    <CheckCircle2 className="w-4 h-4 text-brand" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {delivered.length > 0 && (
                                    <div className="bg-green-900/15 border border-green-700/30 rounded-lg overflow-hidden mb-3" data-testid="customer-delivered-section">
                                        <div className="px-4 py-2 border-b border-green-700/30 text-[10px] uppercase tracking-wider text-green-300 flex items-center gap-1">
                                            <CheckCircle2 className="w-3 h-3" /> Portati al tavolo · buon appetito
                                        </div>
                                        <div className="divide-y divide-green-700/20">
                                            {delivered.map((it) => (
                                                <div key={it.id} className="p-3 flex justify-between text-sm">
                                                    <div className="text-warm">{it.name} <span className="text-elegant">×{it.quantity}</span></div>
                                                    <div className="text-green-400 text-xs font-bold">✓</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {sentItems.length > 0 && (
                                    <div className="bg-deep border border-brand/50 rounded-lg p-4 mt-3 sticky bottom-3" data-testid="customer-live-total">
                                        <div className="flex justify-between items-baseline">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-elegant">Totale conto provvisorio</div>
                                                <div className="text-[10px] text-elegant mt-0.5">
                                                    Incluse {sentItems.length} {sentItems.length === 1 ? "voce" : "voci"} (esclusi eventuali sconti/coperto applicati dalla cassa)
                                                </div>
                                            </div>
                                            <div className="font-display font-bold text-brand text-3xl">€ {liveTotal.toFixed(2)}</div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {tab === "call" && (
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 text-center" data-testid="customer-call-tab">
                        <BellRing className="w-12 h-12 text-brand mx-auto mb-4" />
                        <div className="font-display text-xl font-bold mb-2">Chiama il cameriere</div>
                        <div className="text-elegant text-sm mb-6">Aggiungi una nota se vuoi indicare il motivo.</div>
                        <textarea
                            value={callNote}
                            onChange={(e) => setCallNote(e.target.value)}
                            placeholder="Es: vorrei ordinare un dolce, manca il sale, conto..."
                            data-testid="customer-call-note"
                            className="w-full h-24 p-3 bg-deep border border-[#1f2c44] rounded-md text-warm focus:border-brand focus:outline-none mb-4"
                        />
                        <button onClick={callWaiter} disabled={calling} data-testid="customer-call-button"
                            className="w-full h-14 bg-brand hover:bg-brand-light text-deep font-display font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 disabled:opacity-60">
                            <Bell className="w-5 h-5" /> {calling ? "Invio..." : "Chiama il cameriere"}
                        </button>
                    </div>
                )}
            </div>

            <button onClick={logout} className="fixed bottom-3 right-3 text-xs text-elegant hover:text-warm bg-surface border border-[#1f2c44] rounded-full px-3 py-2 flex items-center gap-1">
                <LogOut className="w-3 h-3" /> Esci
            </button>
        </div>
    );
};

export default CustomerEntry;
